import { useEffect } from "react";
import {
  LuArrowLeft,
  LuRotateCw,
  LuSmartphone,
  LuUpload,
} from "react-icons/lu";
import { Tooltip } from "react-tooltip";
import { components, typography } from "../../design/tokens";
import { ProjectModalProvider } from "../../providers/ProjectModalProvider";
import { debugLog } from "../../utils/logUtils";
import {
  DashboardMode,
  useDashboardControlsActions,
  useDashboardMode,
  useWorkspaceSection,
  WorkspaceSection,
} from "../../view_stores/useDashboardControlsStore";
import { useProjectName } from "../../view_stores/useProjectConfigStore";
import {
  PreviewUrlStatus,
  useProjectPreviewActions,
  useProjectPreviewStatus,
  useProjectPreviewUrl,
} from "../../view_stores/useProjectPreviewStore";
import { ProjectPreviewScreenshotControls } from "../projectPreview/components/ProjectPreviewScreenshotControls";
import { ProjectPreview } from "../projectPreview/ProjectPreview";
import { SideMenuIcon } from "../sideMenu/SideMenuIcon";
import { ProjectChat } from "./components/ProjectChat";
import { ProjectWorkspaceContent } from "./components/ProjectWorkspaceContent";
import { ProjectWorkspaceMenu } from "./components/ProjectWorkspaceMenu";

export function ProjectPageCompact() {
  const { setDashboardMode } = useDashboardControlsActions();
  const previewUrl = useProjectPreviewUrl();
  const previewStatus = useProjectPreviewStatus();

  useEffect(() => {
    if (previewUrl && previewStatus === PreviewUrlStatus.Ready) {
      setDashboardMode(DashboardMode.PREVIEW);
    }
  }, [previewUrl, previewStatus, setDashboardMode]);

  return (
    <>
      <Dashboard />
      <MainView />
    </>
  );
}

function Dashboard() {
  const dashboardMode = useDashboardMode();

  return dashboardMode ? (
    <div
      className="fixed inset-0 z-50 flex flex-col h-[100dvh]
    bg-dark-surface-primary"
    >
      <div
        className="w-full h-full relative
      flex flex-col"
      >
        <ProjectModalProvider>
          <DashboardAppBar />
          <div className="flex-1 overflow-hidden">
            {dashboardMode === DashboardMode.PREVIEW && <ProjectPreview />}
            {dashboardMode === DashboardMode.WORKSPACE && (
              <ProjectWorkspaceContent />
            )}
          </div>
          <Tooltip
            id="dashboard-tooltip"
            className="max-w-[90%]"
            place="top"
            opacity={1.0}
          />
        </ProjectModalProvider>
      </div>
    </div>
  ) : null;
}

function DashboardAppBar() {
  const dashboardMode = useDashboardMode();
  const workspaceSection = useWorkspaceSection();
  const { setDashboardMode, setWorkspaceSection, goBackInCompactView } =
    useDashboardControlsActions();
  const { reload: reloadPreview } = useProjectPreviewActions();

  const getWorkspaceSectionName = () => {
    switch (workspaceSection) {
      case WorkspaceSection.ICON:
        return "App Icon";
      case WorkspaceSection.CODE:
        return "Code";
      case WorkspaceSection.DEBUG:
        return "Debug";
      case WorkspaceSection.SETTINGS:
        return "App Settings";
      case WorkspaceSection.PUBLISH:
        return "Publish";
      case WorkspaceSection.EXPORT:
        return "Export";
    }
  };

  return (
    <div
      className="h-12 w-full px-4 py-9
            flex items-center justify-between gap-2
            border-b border-dark-border"
    >
      <div className="flex items-center gap-4">
        <LuArrowLeft
          size={24}
          onClick={goBackInCompactView}
          className="cursor-pointer"
        />
        <h2 className={`${typography.size.lg} ${typography.weight.semibold}`}>
          {dashboardMode === DashboardMode.WORKSPACE &&
            getWorkspaceSectionName()}
        </h2>
      </div>
      {workspaceSection !== WorkspaceSection.PUBLISH && (
        <div className="flex items-center gap-4">
          {dashboardMode === DashboardMode.PREVIEW && (
            <div className="flex items-center gap-4">
              <ProjectPreviewScreenshotControls optionsPosition="bottom-center" />
              <button
                className="text-dark-text-muted hover:text-dark-text transition-colors p-2"
                onClick={reloadPreview}
                aria-label="Refresh preview"
              >
                <LuRotateCw size={20} />
              </button>
            </div>
          )}
          <button
            className={components.button.primaryCompact}
            onClick={() => {
              setDashboardMode(DashboardMode.WORKSPACE);
              setWorkspaceSection(WorkspaceSection.PUBLISH);
            }}
          >
            <LuUpload size={16} />
            Publish
          </button>
        </div>
      )}
    </div>
  );
}

function MainView() {
  return (
    <div
      className="h-[100dvh] min-h-[500px] w-full
    bg-dark-surface-primary
    flex flex-col"
    >
      <MainViewAppBar />
      <ProjectChat />
    </div>
  );
}

function MainViewAppBar() {
  const projectName = useProjectName();
  const previewUrl = useProjectPreviewUrl();
  const previewStatus = useProjectPreviewStatus();
  const { setDashboardMode } = useDashboardControlsActions();

  // DEBUG: Track when projectName or previewUrl changes in compact view
  debugLog(
    `[DEBUG ${new Date().toISOString()}] ProjectPageCompact MainViewAppBar render - projectName:`,
    projectName,
    "previewUrl:",
    previewUrl,
    "previewStatus:",
    previewStatus
  );

  return (
    <div
      className="pt-[18px] pb-1 w-full px-2
      flex flex-col gap-3
      bg-dark-surface-primary
      border-b border-dark-border
      relative z-40"
    >
      <div className="flex items-center justify-between w-full">
        <SideMenuIcon />
        <div className="flex gap-2">
          <ProjectWorkspaceMenu />
          <button
            onClick={() => setDashboardMode(DashboardMode.PREVIEW)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md
            ${typography.size.sm} ${typography.weight.medium} text-dark-text`}
            disabled={
              !projectName || previewStatus === PreviewUrlStatus.NotAvailable
            }
          >
            <LuSmartphone size={18} />
            Preview
          </button>
        </div>
      </div>
      <div
        className={`${typography.size.base} ${typography.weight.semibold} px-1 pb-2
      whitespace-nowrap overflow-x-auto`}
      >
        {projectName}
      </div>
    </div>
  );
}
